import { ReactNode } from "react";
import {
  Block,
  Mail,
  Policy,
  SafetyCheck,
  ScheduleSend,
  VerifiedUser,
} from "@mui/icons-material";
import { Alert, AlertTitle } from "@mui/material";

import {
  additionalAssistanceClause,
  getGeneralErrorAlert,
  getLookupAlert,
  getRecaptchaAlert,
  getTokenValidationErrorAlert,
} from "@/components/alerts/global";

import { UserIndicator } from "@/types/user";

function getExpiredTokenAlert(userIndicator: UserIndicator) {
  return (
    <Alert icon={<Block />} severity="error">
      <AlertTitle>Your security settings reset has expired</AlertTitle>
      <strong>The reset must be completed within 15 minutes</strong>
      {restartClause()}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

// lookup success - step 1
function getLookupSuccessAlert(userIndicator: UserIndicator) {
  return (
    <Alert severity="warning" icon={<Policy />}>
      <AlertTitle>Your account information has been found</AlertTitle>
      <strong>
        Please set the answer to your security question then click
        &quot;Confirm&quot;.
      </strong>
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

function getSecurityAnswerSuccessAlert(userIndicator: UserIndicator) {
  return (
    <Alert severity="warning" icon={<Policy />}>
      <AlertTitle>
        Your security question has been answered successfully
      </AlertTitle>
      <strong>
        Almost there! Just set your password and then click &quot;Complete&quot;
        to finish.
      </strong>
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

// reset success - step 3
function getResetSuccessAlert(
  userIndicator: UserIndicator,
  isFullReset: boolean = false,
) {
  return (
    <Alert icon={<VerifiedUser />} severity="success">
      <AlertTitle>Congratulations!</AlertTitle>
      You have successfully reset your MPTC account{" "}
      {isFullReset ? "security settings and" : ""} password. Use your new
      password for college systems your account is allowed to access.
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

function getSecurityAnswerBlockedAlert(userIndicator: UserIndicator) {
  return (
    <Alert severity="error">
      <AlertTitle>Failed to validate your security answer</AlertTitle>
      You have been temporarily blocked from trying again. You can reset your
      security settings at any time by clicking &quot;I do not remember my
      security answer&quot;.
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

// send email verification success - step 1
function getSendEmailSuccessAlert(
  email: string,
  userIndicator: UserIndicator,
  timerElement: ReactNode,
) {
  return (
    <Alert severity="success" icon={<Mail />}>
      <AlertTitle>Security settings email sent</AlertTitle>
      <strong>
        Check your {email} email inbox for an email titled &quot;Reset your
        password and security settings&quot;.
      </strong>
      <br />
      <br />
      {getTokenValidationStatus(timerElement)}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

function getSetupCompletionStatus(timerElement: ReactNode) {
  const content = timerElement ? (
    <strong>
      You have {timerElement} to complete your security settings reset.
    </strong>
  ) : (
    <strong>
      Your security settings reset has expired. Please restart the password
      reset process.
    </strong>
  );

  return (
    <>
      <br />
      <br />
      {content}
    </>
  );
}

function getUserEmailsErrorAlert(userIndicator: UserIndicator) {
  return (
    <Alert severity="error" icon={<Mail />}>
      <AlertTitle>
        Unable to find any valid emails for security settings reset
      </AlertTitle>
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

function getUserEmailsSuccessAlert(
  userIndicator: UserIndicator,
  missingQuestion: boolean,
) {
  const title = missingQuestion
    ? "You need to setup your security question and answer"
    : "You forgot the answer to your security question";

  return (
    <Alert severity="warning" icon={<ScheduleSend />}>
      <AlertTitle>{title}</AlertTitle>
      <strong>
        Select an email below to send your verification link. The link will
        remain valid for 15 minutes after it is sent.
      </strong>
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

function getTokenValidationStatus(timerElement: ReactNode) {
  if (!timerElement)
    return (
      <strong>
        The link from your email has expired or already been used.
      </strong>
    );
  else
    return (
      <strong>
        The link from your email is valid for {timerElement} and can only be
        used once to reset your security settings.
      </strong>
    );
}

function getVerificationSuccessAlert(
  userIndicator: UserIndicator,
  timerElement: ReactNode,
  isFullReset: boolean = false,
) {
  return (
    <Alert severity="warning" icon={<SafetyCheck />}>
      <AlertTitle>Your security settings reset has been verified</AlertTitle>
      <strong>
        Almost there! Just set your security settings and password, then click
        &quot;Complete&quot; to finish.
      </strong>
      {getSetupCompletionStatus(timerElement)}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

function restartClause() {
  const restartUrl = `${window.location.origin}/account-utilities/password-reset`;

  return (
    <>
      <br />
      <br />
      You can click <a href={restartUrl}>here</a> to restart at any time.
    </>
  );
}

const map = new Map<number, ReactNode>([
  [0, getLookupAlert()],
  [1, null],
  [2, null],
]);

const passwordResetAlerts = {
  getExpiredTokenAlert,
  getGeneralErrorAlert,
  getLookupAlert,
  getLookupSuccessAlert,
  getRecaptchaAlert,
  getResetSuccessAlert,
  getSecurityAnswerBlockedAlert,
  getSecurityAnswerSuccessAlert,
  getSendEmailSuccessAlert,
  getTokenValidationErrorAlert,
  getUserEmailsErrorAlert,
  getUserEmailsSuccessAlert,
  getVerificationSuccessAlert,
  map,
};

export default passwordResetAlerts;
